import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

import { InputText } from 'primeng/inputtext'
import { BehaviorSubject } from 'rxjs'

@Component({
  selector: 'app-password-input',
  templateUrl: 'password-input.component.html',
  styleUrl: 'password-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordInputComponent),
      multi: true,
    },
  ],
  imports: [InputText, AsyncPipe],
})
export class PasswordInputComponent implements ControlValueAccessor {
  protected onChange: (value: any) => void = () => undefined
  protected onTouched: () => void = () => undefined

  protected readonly changeDetector = inject(ChangeDetectorRef)
  protected readonly isPasswordRevealed$ = new BehaviorSubject<boolean>(false)

  protected touched = false
  protected readonly isDisabled = signal(false)
  protected readonly _value = signal('')

  @Input() height?: number
  @Input() placeholder = ''
  @Input() set value(value: string) {
    this._value.set(value)
  }

  get value() {
    return this._value()
  }

  @Input() inputmode = ''

  @ViewChild('input') input!: ElementRef

  getHeight() {
    return this.height + 'px'
  }

  onPasswordPaste(event: ClipboardEvent) {
    event.preventDefault()
    // prevent potential issues on iOS
    const pastedText = event.clipboardData?.getData('text') || ''
    this.writeValue(pastedText.trim())
  }

  public togglePasswordReveal() {
    if (!this.isDisabled()) {
      this.isPasswordRevealed$.next(!this.isPasswordRevealed$.value)
    }
  }

  public writeValue(value: any) {
    this.onChange(value)
    this.value = value
  }

  public registerOnChange(onChange: () => void) {
    this.onChange = onChange
  }

  public registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched
  }

  public setDisabledState(disabled: boolean) {
    this.isDisabled.set(disabled)
  }

  protected onInputChanged() {
    this.markAsTouched()
    this.writeValue(this.input.nativeElement.value)
  }

  protected markAsTouched() {
    if (!this.touched) {
      this.onTouched()
      this.touched = true
    }
  }
}
